import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Trash, CheckCircle, XCircle } from "@phosphor-icons/react";
import { api, adminHeaders } from "@/lib/api";

const DEPT_LABEL = {
    kantor: "Checklist Kantor",
    workshop: "Checklist Workshop",
    progress: "Foto Progress",
    service: "Foto Service",
    status: "Status Ruangan",
    reminder: "Reminder Checklist",
    reminder_kantor: "Reminder Kantor",
    reminder_workshop: "Reminder Workshop",
};

const fmtWib = (iso) =>
    new Date(iso).toLocaleString("id-ID", {
        timeZone: "Asia/Jakarta",
        day: "numeric",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
    });

export const WaSettings = ({ onClose }) => {
    const [waContacts, setWaContacts] = useState(null);
    const [waBaseUrl, setWaBaseUrl] = useState("");
    const [waBusy, setWaBusy] = useState(false);
    const [waSenders, setWaSenders] = useState(null);
    const [waSendersBusy, setWaSendersBusy] = useState(false);
    const [waWebhookUrl, setWaWebhookUrl] = useState("");
    const [waLogs, setWaLogs] = useState([]);

    useEffect(() => {
        api.get("/admin/wa/contacts", { headers: adminHeaders() })
            .then((res) => { setWaContacts(res.data.contacts); setWaBaseUrl(res.data.public_base_url || ""); })
            .catch(() => toast.error("Gagal memuat kontak WA"));
        api.get("/admin/wa/senders", { headers: adminHeaders() })
            .then((res) => setWaSenders(res.data.senders))
            .catch(() => {});
        api.get("/admin/wa/webhook-info", { headers: adminHeaders() })
            .then((res) => setWaWebhookUrl(res.data.url || ""))
            .catch(() => {});
        api.get("/admin/wa/logs", { headers: adminHeaders() })
            .then((res) => setWaLogs(res.data))
            .catch(() => {});
    }, []);

    const saveWaContacts = async () => {
        setWaBusy(true);
        try {
            await api.put("/admin/wa/contacts", waContacts, { headers: adminHeaders() });
            await api.put("/admin/wa/base-url", { base_url: waBaseUrl }, { headers: adminHeaders() });
            toast.success("Pengaturan WA disimpan");
        } catch {
            toast.error("Gagal menyimpan kontak WA");
        } finally {
            setWaBusy(false);
        }
    };

    const saveWaSenders = async () => {
        setWaSendersBusy(true);
        try {
            await api.put("/admin/wa/senders", { senders: waSenders }, { headers: adminHeaders() });
            toast.success("Daftar pengirim disimpan");
        } catch {
            toast.error("Gagal menyimpan daftar pengirim");
        } finally {
            setWaSendersBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="wa-settings-overlay">
            <div className="bg-white border border-border w-full max-w-md max-h-[90vh] overflow-y-auto p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between">
                    <div>
                        <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground">Pengaturan</p>
                        <h2 className="font-serif text-2xl tracking-tight font-medium mt-1">WhatsApp</h2>
                    </div>
                    <button data-testid="wa-settings-close" onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" title="Tutup">
                        <X size={20} weight="light" />
                    </button>
                </div>

                <div data-testid="wa-contacts-section">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">Kontak Notifikasi WhatsApp</p>
                    <p className="text-xs text-muted-foreground mb-3">Penerima notifikasi checklist, status ruangan, foto progress & service, dan pengingat otomatis jam 16:30 WIB (hari kerja).</p>
                    {!waContacts ? (
                        <p className="text-xs text-muted-foreground">Memuat kontak...</p>
                    ) : (
                        <div className="space-y-2">
                            <label className="block">
                                <span className="text-xs text-muted-foreground">Alamat publik untuk link di pesan WA</span>
                                <input
                                    data-testid="wa-base-url"
                                    value={waBaseUrl}
                                    onChange={(e) => setWaBaseUrl(e.target.value.trim())}
                                    placeholder="https://report.inerre.com"
                                    className="mt-1 w-full border border-border px-2 py-1.5 text-sm outline-none focus:border-primary bg-transparent font-mono"
                                />
                            </label>
                            {[
                                ["kantor", "Checklist Kantor"],
                                ["workshop", "Checklist Workshop"],
                                ["owner", "Foto Progress (Owner)"],
                                ["supervisor", "Supervisor Lapangan"],
                            ].map(([key, label]) => (
                                <div key={key} className="flex items-center gap-2" data-testid={`wa-contact-${key}`}>
                                    <span className="text-xs text-muted-foreground w-28 shrink-0">{label}</span>
                                    <input
                                        data-testid={`wa-name-${key}`}
                                        value={waContacts[key].name}
                                        onChange={(e) => setWaContacts({ ...waContacts, [key]: { ...waContacts[key], name: e.target.value } })}
                                        placeholder="Nama"
                                        className="flex-1 min-w-0 border border-border px-2 py-1.5 text-sm outline-none focus:border-primary bg-transparent"
                                    />
                                    <input
                                        data-testid={`wa-phone-${key}`}
                                        value={waContacts[key].phone}
                                        onChange={(e) => setWaContacts({ ...waContacts, [key]: { ...waContacts[key], phone: e.target.value.replace(/[^\d]/g, "") } })}
                                        placeholder="08xxxxxxxxxx"
                                        className="w-36 border border-border px-2 py-1.5 text-sm outline-none focus:border-primary bg-transparent font-mono"
                                    />
                                </div>
                            ))}
                            <button
                                data-testid="wa-contacts-save"
                                onClick={saveWaContacts}
                                disabled={waBusy}
                                className="border border-primary text-primary px-4 py-1.5 text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50"
                            >
                                {waBusy ? "Menyimpan..." : "Simpan Kontak"}
                            </button>
                        </div>
                    )}
                </div>

                <div className="border-t border-border pt-4" data-testid="wa-inbox-section">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">Foto Progress via Grup WhatsApp</p>
                    <p className="text-xs text-muted-foreground mb-3">
                        Anggota terdaftar chat langsung ke nomor sistem (atau lewat grup): sistem membalas daftar ruangan "Barang Terkirim" bernomor — balas ANGKANYA untuk memilih ruangan, lalu semua foto yang dikirim otomatis masuk Foto Progress ruangan itu. Pindah ruangan kapan saja (tanpa menunggu sesi habis): ketik nama ruangan lain atau kirim "daftar". Sesi aktif kedaluwarsa setelah 30 menit tanpa kirim foto. Kontak di atas otomatis terdaftar.
                    </p>
                    {!waSenders ? (
                        <p className="text-xs text-muted-foreground">Memuat...</p>
                    ) : (
                        <div className="space-y-2">
                            {waSenders.map((s2, i) => (
                                <div key={i} className="flex items-center gap-2" data-testid={`wa-sender-${i}`}>
                                    <input
                                        data-testid={`wa-sender-name-${i}`}
                                        value={s2.name}
                                        onChange={(e) => setWaSenders(waSenders.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))}
                                        placeholder="Nama"
                                        className="flex-1 min-w-0 border border-border px-2 py-1.5 text-sm outline-none focus:border-primary bg-transparent"
                                    />
                                    <input
                                        data-testid={`wa-sender-phone-${i}`}
                                        value={s2.phone}
                                        onChange={(e) => setWaSenders(waSenders.map((x, j) => (j === i ? { ...x, phone: e.target.value.replace(/[^\d]/g, "") } : x)))}
                                        placeholder="08xxxxxxxxxx"
                                        className="w-36 border border-border px-2 py-1.5 text-sm outline-none focus:border-primary bg-transparent font-mono"
                                    />
                                    <button
                                        data-testid={`wa-sender-del-${i}`}
                                        onClick={() => setWaSenders(waSenders.filter((_, j) => j !== i))}
                                        className="text-muted-foreground hover:text-red-600 transition-colors shrink-0"
                                        title="Hapus"
                                    >
                                        <Trash size={16} weight="light" />
                                    </button>
                                </div>
                            ))}
                            <div className="flex gap-2">
                                <button
                                    data-testid="wa-sender-add"
                                    onClick={() => setWaSenders([...waSenders, { name: "", phone: "" }])}
                                    className="border border-border px-3 py-1.5 text-xs hover:border-primary hover:text-primary transition-colors"
                                >
                                    + Tambah Pengirim
                                </button>
                                <button
                                    data-testid="wa-senders-save"
                                    onClick={saveWaSenders}
                                    disabled={waSendersBusy}
                                    className="border border-primary text-primary px-4 py-1.5 text-xs font-medium hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50"
                                >
                                    {waSendersBusy ? "Menyimpan..." : "Simpan Pengirim"}
                                </button>
                            </div>
                        </div>
                    )}
                    {waWebhookUrl && (
                        <div className="mt-3">
                            <span className="text-xs text-muted-foreground">Tempel URL ini di Fonnte → Device → Edit → Webhook (Autoread: ON):</span>
                            <div className="mt-1 flex gap-2">
                                <input
                                    readOnly
                                    data-testid="wa-webhook-url"
                                    value={waWebhookUrl}
                                    onFocus={(e) => e.target.select()}
                                    className="flex-1 min-w-0 border border-border px-2 py-1.5 text-xs font-mono bg-secondary outline-none"
                                />
                                <button
                                    data-testid="wa-webhook-copy"
                                    onClick={() => { navigator.clipboard?.writeText(waWebhookUrl); toast.success("URL webhook disalin"); }}
                                    className="border border-border px-3 py-1.5 text-xs hover:border-primary hover:text-primary transition-colors shrink-0"
                                >
                                    Salin
                                </button>
                            </div>
                        </div>
                    )}
                </div>

                <div className="border-t border-border pt-4" data-testid="wa-logs-section">
                    <p className="text-xs tracking-[0.2em] uppercase font-bold text-muted-foreground mb-1">Log Notifikasi</p>
                    <p className="text-xs text-muted-foreground mb-3">50 notifikasi WA terakhir yang dikirim sistem.</p>
                    {waLogs.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Belum ada notifikasi terkirim.</p>
                    ) : (
                        <div className="space-y-1.5 max-h-64 overflow-y-auto" data-testid="wa-logs-list">
                            {waLogs.map((l, i) => (
                                <div key={i} className="flex items-start gap-2 border border-border px-2.5 py-2" data-testid={`wa-log-${i}`}>
                                    {l.ok ? (
                                        <CheckCircle size={15} weight="fill" className="text-green-600 mt-0.5 shrink-0" />
                                    ) : (
                                        <XCircle size={15} weight="fill" className="text-red-600 mt-0.5 shrink-0" />
                                    )}
                                    <div className="min-w-0">
                                        <p className="text-xs font-medium">
                                            {DEPT_LABEL[l.dept] || l.dept} → {l.name}
                                            <span className="text-muted-foreground font-normal"> · {fmtWib(l.at)}</span>
                                        </p>
                                        <p className="text-xs text-muted-foreground break-words">{l.message}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};
